// Bluetooth-LE bridge to Shelly Gen2+ devices (Phase 4.5, Step 2).
//
// Runs inside the native Capacitor shell (the technician APK). In a
// plain browser there's no Bluetooth, so callers must gate on
// `isNativeApp()` and fall back to the manual wizard.
//
// Shelly Gen2/3/4 expose the Mongoose-OS RPC service over GATT. A
// request is a JSON-RPC frame sent as: write the 4-byte big-endian
// length to the TX-control characteristic, then write the JSON bytes
// to the DATA characteristic (chunked). The response is read back:
// poll the RX-control characteristic for the 4-byte length, then read
// the DATA characteristic repeatedly until that many bytes are
// assembled. Same framing for every method, so `rpcCall` below is the
// single primitive both Step 2a (read GetDeviceInfo) and Step 2b
// (write Wifi/MQTT/Switch config) build on.

import { Capacitor } from '@capacitor/core';
import { BleClient } from '@capacitor-community/bluetooth-le';

// Mongoose-OS RPC GATT UUIDs. The hex spells the ASCII tags
// ("_mOS_RPC_SVC_ID_", "_mOS_RPC_data___", …) — handy when sanity-
// checking against a BLE sniffer.
const RPC_SERVICE = '5f6d4f53-5f52-5043-5f53-56435f49445f';
const RPC_DATA = '5f6d4f53-5f52-5043-5f64-6174615f5f5f';
const RPC_RX_CTL = '5f6d4f53-5f52-5043-5f72-785f63746c5f'; // response length (read)
const RPC_TX_CTL = '5f6d4f53-5f52-5043-5f74-785f63746c5f'; // request length (write)

// Conservative write chunk. Android usually negotiates a larger MTU,
// but 20 bytes is the safe floor that works before any negotiation.
const WRITE_CHUNK = 20;

export function isNativeApp() {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

let _initialized = false;

export async function ensureBleReady() {
  if (!_initialized) {
    // androidNeverForLocation:false → take the location-backed scan
    // path, which works on every Android version without a hand-edited
    // manifest. The user may be asked to grant location + turn GPS on
    // for the scan; Step 2b can tighten this to neverForLocation.
    await BleClient.initialize({ androidNeverForLocation: false });
    _initialized = true;
  }
}

// --- scanning ---------------------------------------------------------------

// Scan for nearby Shelly units for `durationMs`, calling `onDevices`
// with the running list (deduped, strongest signal first) as results
// arrive. Resolves with the final sorted list when the scan stops.
export async function scanShellies({ onDevices, durationMs = 8000 } = {}) {
  await ensureBleReady();
  const byId = new Map();

  const emit = () => {
    const list = [...byId.values()].sort((a, b) => b.rssi - a.rssi);
    if (onDevices) onDevices(list);
  };

  await BleClient.requestLEScan({ allowDuplicates: true }, (result) => {
    const name = result.localName || result.name || result.device?.name || '';
    if (!/^shelly/i.test(name)) return; // only surface Shelly units
    const id = result.device?.deviceId;
    if (!id) return;
    const prev = byId.get(id);
    // Keep the strongest RSSI we've seen for this device.
    const rssi = typeof result.rssi === 'number' ? result.rssi : (prev?.rssi ?? -999);
    byId.set(id, {
      deviceId: id,
      name: name || prev?.name || 'Shelly',
      rssi: prev ? Math.max(prev.rssi, rssi) : rssi,
    });
    emit();
  });

  await new Promise((r) => setTimeout(r, durationMs));
  try { await BleClient.stopLEScan(); } catch { /* already stopped */ }
  const final = [...byId.values()].sort((a, b) => b.rssi - a.rssi);
  if (onDevices) onDevices(final);
  return final;
}

// --- RPC over GATT ----------------------------------------------------------

function encodeFrame(method, params) {
  const payload = JSON.stringify({ id: Date.now() % 100000, src: 'huri-ble', method, params: params || {} });
  return new TextEncoder().encode(payload);
}

function lengthDataView(n) {
  const dv = new DataView(new ArrayBuffer(4));
  dv.setUint32(0, n, false); // big-endian
  return dv;
}

function readUint32BE(dataView) {
  if (!dataView || dataView.byteLength < 4) return 0;
  return dataView.getUint32(0, false);
}

async function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

async function sha256Hex(str) {
  const data = new TextEncoder().encode(str);
  const buf = await crypto.subtle.digest('SHA-256', data);
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

// Send one JSON-RPC call to a connected device and return the parsed
// `result` (throws on an RPC error). `onStage` reports progress so the
// UI can show where things are if hardware misbehaves.
export async function rpcCall(deviceId, method, params, { onStage, responseTimeoutMs = 5000 } = {}) {
  const frame = encodeFrame(method, params);

  // 1) announce request length
  await BleClient.write(deviceId, RPC_SERVICE, RPC_TX_CTL, lengthDataView(frame.length));

  // 2) stream the JSON frame in chunks
  for (let off = 0; off < frame.length; off += WRITE_CHUNK) {
    const slice = frame.slice(off, off + WRITE_CHUNK);
    await BleClient.write(deviceId, RPC_SERVICE, RPC_DATA, new DataView(slice.buffer, slice.byteOffset, slice.byteLength));
  }

  // 3) wait for the device to publish a response length. Most calls
  // answer in well under a second; Wifi.Scan can take several seconds
  // while the radio sweeps, so callers bump responseTimeoutMs.
  if (onStage) onStage('awaiting_response');
  let respLen = 0;
  const tries = Math.max(1, Math.round(responseTimeoutMs / 100));
  for (let i = 0; i < tries && respLen === 0; i++) {
    await sleep(100);
    respLen = readUint32BE(await BleClient.read(deviceId, RPC_SERVICE, RPC_RX_CTL));
  }
  if (respLen === 0) throw new Error('הרכיב לא החזיר תשובה (timeout)');

  // 4) assemble the response from repeated DATA reads
  const chunks = [];
  let got = 0;
  for (let i = 0; i < 200 && got < respLen; i++) {
    const dv = await BleClient.read(deviceId, RPC_SERVICE, RPC_DATA);
    if (dv && dv.byteLength) {
      chunks.push(new Uint8Array(dv.buffer.slice(dv.byteOffset, dv.byteOffset + dv.byteLength)));
      got += dv.byteLength;
    } else {
      await sleep(50);
    }
  }

  const all = new Uint8Array(got);
  let pos = 0;
  for (const c of chunks) { all.set(c, pos); pos += c.length; }
  const text = new TextDecoder().decode(all);
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error('תשובה לא קריאה מהרכיב');
  }
  if (parsed.error) {
    throw new Error(parsed.error.message || 'הרכיב החזיר שגיאה');
  }
  return parsed.result;
}

// Step 2a: connect, read identity, disconnect. Proves the whole BLE +
// GATT-RPC pipeline end to end without changing anything on the device.
export async function identifyShelly(deviceId, { onStage } = {}) {
  await ensureBleReady();
  if (onStage) onStage('connecting');
  await BleClient.connect(deviceId, () => {});
  try {
    if (onStage) onStage('reading');
    const info = await rpcCall(deviceId, 'Shelly.GetDeviceInfo', {}, { onStage });
    return info;
  } finally {
    try { await BleClient.disconnect(deviceId); } catch { /* ignore */ }
  }
}

// Ask the Shelly to scan for WiFi networks IT can see and return them
// strongest-first, deduped by SSID. Done from the device's own vantage
// point (it's mounted at the gate), which is exactly the set of networks
// relevant for it to join — better than scanning from the phone. Lets
// the technician pick the SSID from a list instead of typing it.
export async function scanWifiNetworks(deviceId, { onStage } = {}) {
  await ensureBleReady();
  if (onStage) onStage('connecting');
  await BleClient.connect(deviceId, () => {});
  try {
    if (onStage) onStage('wifi_scanning');
    const res = await rpcCall(deviceId, 'Wifi.Scan', {}, { responseTimeoutMs: 15000 });
    const results = (res && res.results) || [];
    const bySsid = new Map();
    for (const ap of results) {
      const ssid = (ap.ssid || '').trim();
      if (!ssid) continue; // skip hidden networks
      const prev = bySsid.get(ssid);
      const rssi = typeof ap.rssi === 'number' ? ap.rssi : -999;
      if (!prev || rssi > prev.rssi) {
        bySsid.set(ssid, { ssid, rssi, open: ap.auth === 0 || ap.auth === 'OPEN' });
      }
    }
    return [...bySsid.values()].sort((a, b) => b.rssi - a.rssi);
  } finally {
    try { await BleClient.disconnect(deviceId); } catch { /* ignore */ }
  }
}

// Phase 4.5 — try to join a KNOWN site network without showing the
// manual picker. Scans from the device; if the saved SSID is in range,
// sets the STA config and polls Wifi.GetStatus until the device gets an
// IP (joined) or the timeout lapses. Returns:
//   { joined: true }                      — connected, caller proceeds
//   { joined: false, reason: 'not_found' } — saved SSID not in scan
//   { joined: false, reason: 'timeout' }   — set but never got an IP
//   { joined: false, reason: 'error', message } — RPC failure
// On any non-joined result the caller falls back to the manual picker.
// Leaves the BLE link OPEN on success so provisioning continues on the
// same session; disconnects on failure paths.
export async function tryConnectKnownWifi(deviceId, { ssid, password, timeoutMs = 15000, onStage } = {}) {
  await ensureBleReady();
  if (onStage) onStage('connecting');
  await BleClient.connect(deviceId, () => {});
  let keepOpen = false;
  try {
    if (onStage) onStage('wifi_scanning');
    let inRange = false;
    try {
      const res = await rpcCall(deviceId, 'Wifi.Scan', {}, { responseTimeoutMs: 15000 });
      const results = (res && res.results) || [];
      inRange = results.some((ap) => (ap.ssid || '').trim() === ssid);
    } catch (e) {
      return { joined: false, reason: 'error', message: e.message };
    }
    if (!inRange) {
      return { joined: false, reason: 'not_found' };
    }

    if (onStage) onStage('wifi_joining');
    await rpcCall(deviceId, 'Wifi.SetConfig', {
      config: { sta: { ssid, pass: password, enable: true } },
    });

    // Poll Wifi.GetStatus until it explicitly reports "got ip" with a real
    // IP. Two pitfalls the earlier version fell into:
    //   1. `sta_ip` can be the STRING "0.0.0.0" while the device is still
    //      trying — that's truthy in JS, so a naive `if (sta_ip)` returned
    //      success on a failing join.
    //   2. Mongoose-OS reports `status` distinctly: "connecting" /
    //      "associating" / "got ip" / "disconnected" etc. Only "got ip"
    //      means actual usable connectivity.
    // We also bail early on explicit failure words ("fail" / "reject" /
    // "auth") so a wrong password doesn't burn the full timeout.
    const deadline = Date.now() + timeoutMs;
    let lastStatus = null;
    while (Date.now() < deadline) {
      await sleep(1500);
      try {
        const st = await rpcCall(deviceId, 'Wifi.GetStatus', {}, { responseTimeoutMs: 4000 });
        lastStatus = st;
        const ip = ((st && st.sta_ip) || '').trim();
        const status = ((st && st.status) || '').toLowerCase();
        const ipOk = ip && ip !== '0.0.0.0' && !ip.startsWith('169.254.');
        const statusOk = status === 'got ip' || status === 'got_ip';
        if (ipOk && statusOk) {
          keepOpen = true;
          return { joined: true };
        }
        if (status && (status.includes('fail') || status.includes('reject') ||
                       status.includes('auth') || status === 'wrong_password')) {
          return { joined: false, reason: 'auth', status, message: status };
        }
      } catch { /* transient — keep polling */ }
    }
    const finalStatus = (lastStatus && lastStatus.status) || '';
    const finalIp = (lastStatus && lastStatus.sta_ip) || '';
    return {
      joined: false,
      reason: 'timeout',
      status: finalStatus,
      message: finalStatus ? `סטטוס אחרון: ${finalStatus}${finalIp ? ` (IP: ${finalIp})` : ''}` : '',
    };
  } finally {
    if (!keepOpen) {
      try { await BleClient.disconnect(deviceId); } catch { /* ignore */ }
    }
  }
}

// Pull the 12-hex MAC out of a Shelly BLE advertisement name, e.g.
// "Shelly2PMG4-58E6C5312B94" -> "58E6C5312B94". Returns '' if absent.
export function macFromName(name) {
  const m = /([0-9A-Fa-f]{12})\s*$/.exec(name || '');
  return m ? m[1].toUpperCase() : '';
}

async function rebootDevice(deviceId, { ipAddr } = {}) {
  // Reboot Shelly Gen4 reliably. The hard-learned lesson: on Gen4 firmware
  // 1.5.99 every SetConfig (MQTT, Cloud, Wifi-AP) stays PENDING in RAM
  // until an actual reboot writes it to flash and re-initializes the
  // subsystems — without a reboot, the technician's Shelly UI shows the
  // factory-default empty MQTT page, the broker never sees the device,
  // and the wizard hangs on "waiting for Shelly to connect to server".
  //
  // BLE Shelly.Reboot alone is unreliable: the link can be in a transient
  // bad state right after a Wifi.SetConfig, so the RPC fails silently and
  // no reboot happens. We fix this by firing the reboot on TWO paths in
  // parallel, whichever lands first:
  //   1. BLE Shelly.Reboot with delay_ms=100 (almost immediate, fires
  //      before the caller's BLE disconnect can race it).
  //   2. HTTP POST /rpc/Shelly.Reboot directly to the device's LAN IP.
  //      Bulletproof when the technician's phone is on the same WiFi as
  //      the device; harmless (fast fail) when phone is on cellular.
  // After firing both, we sleep long enough for the reboot countdown to
  // actually expire before our caller closes the BLE handle.
  const fireBle = (async () => {
    try {
      await rpcCall(deviceId, 'Shelly.Reboot', { delay_ms: 100 }, { responseTimeoutMs: 2500 });
    } catch { /* device often drops link mid-reply — that's the goal */ }
  })();

  const fireHttp = (async () => {
    if (!ipAddr) return;
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 2500);
      await fetch(`http://${ipAddr}/rpc/Shelly.Reboot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ delay_ms: 100 }),
        signal: ctrl.signal,
      });
      clearTimeout(t);
    } catch { /* phone on cellular, or device unreachable — BLE path covers it */ }
  })();

  await Promise.all([fireBle, fireHttp]);
  // Hold the BLE link open long enough for the actual reboot to commence
  // (delay_ms=100 + Shelly boot teardown ~3 s).
  await sleep(3500);
}

async function connectWithRetry(deviceId, { tries = 8, delayMs = 3000 } = {}) {
  let lastErr;
  for (let i = 0; i < tries; i++) {
    try { await BleClient.connect(deviceId, () => {}); return; }
    catch (e) { lastErr = e; await sleep(delayMs); }
  }
  throw lastErr || new Error('לא ניתן להתחבר לרכיב');
}

// Step 2b: push the full Huri-Connect configuration to a Shelly over
// Bluetooth, then let it join WiFi + our MQTT broker on its own.
//
// The 2PM Gen4 ships in the "cover" profile, and our command layer needs
// "switch". Switching the profile is a dedicated RPC — Shelly.SetProfile
// {name:"switch"} — that only takes effect after a reboot, and the
// Switch:0/1 components we detach don't exist until then. So when the
// device isn't already in switch mode we do it as phase 1 (SetProfile +
// reboot + reconnect); phase 2 then writes name + detached + MQTT + WiFi
// and reboots once more to bring it all up together.
//
// Critical steps (MQTT, WiFi) throw on failure; the rest are best-effort
// with warnings (the wizard's open/close test is the real proof).
// Returns { warnings: string[] }.
export async function provisionShelly(deviceId, { ssid, password, creds, deviceName, onStage } = {}) {
  await ensureBleReady();
  const warnings = [];
  const prefix = creds.mqtt_username;
  const port = creds.broker_port || 8883;
  const server = `${creds.broker_host}:${port}`;
  const name = deviceName || prefix;

  // --- Phase 1: ensure the device is in the "switch" profile ---
  if (onStage) onStage('connecting');
  await connectWithRetry(deviceId, { tries: 4, delayMs: 2000 });
  let needsReconnect = false;
  let realm = null; // device id, used as the SHA realm for Shelly.SetAuth
  try {
    if (onStage) onStage('checking');
    let profile;
    try {
      const info = await rpcCall(deviceId, 'Shelly.GetDeviceInfo', {});
      profile = info && info.profile;
      realm = info && info.id;
    } catch (e) { warnings.push('קריאת מצב הרכיב נכשלה: ' + e.message); }

    // Only multi-profile devices report a profile string. Switch only if
    // it's reported and not already "switch".
    if (typeof profile === 'string' && profile !== 'switch') {
      if (onStage) onStage('config_profile');
      try {
        await rpcCall(deviceId, 'Shelly.SetProfile', { name: 'switch' });
      } catch (e) { warnings.push('מעבר למצב Switch נכשל: ' + e.message); }
      if (onStage) onStage('rebooting_profile');
      await rebootDevice(deviceId);
      needsReconnect = true;
    }
  } finally {
    if (needsReconnect) {
      try { await BleClient.disconnect(deviceId); } catch { /* ignore */ }
    }
  }

  if (needsReconnect) {
    // Wait out the profile reboot, then reconnect (now in switch mode).
    if (onStage) onStage('reconnecting');
    await sleep(8000);
    await connectWithRetry(deviceId, { tries: 12, delayMs: 3000 });
  }

  // --- Phase 2: name, detached inputs, MQTT, cloud, WiFi, reboot ---
  try {
    if (onStage) onStage('config_name');
    try {
      await rpcCall(deviceId, 'Sys.SetConfig', { config: { device: { name } } });
    } catch (e) { warnings.push('שם המכשיר לא נקבע: ' + e.message); }

    // Detach both inputs from their relays (a limit switch on S1 must
    // not self-trigger O1). Possible now that we're in switch mode.
    // NOTE: in_mode "detached" is an INVALID combination with the
    // default initial_state "match_input" (the input can't drive the
    // relay it's detached from), so we set initial_state in the same
    // call. "off" is the safe choice for our pulse relays — the output
    // starts off on every boot, never firing a stray pulse or lock.
    if (onStage) onStage('config_inputs');
    for (const id of [0, 1]) {
      try {
        await rpcCall(deviceId, 'Switch.SetConfig', {
          id,
          config: { in_mode: 'detached', initial_state: 'off' },
        });
      } catch (e) { warnings.push(`ניתוק קלט ${id} נכשל: ` + e.message); }
    }

    // Critical: MQTT to our broker with TLS verification.
    if (onStage) onStage('config_mqtt');
    await rpcCall(deviceId, 'MQTT.SetConfig', {
      config: {
        enable: true,
        server,
        user: creds.broker_username,
        pass: creds.broker_password,
        topic_prefix: prefix,
        ssl_ca: 'ca.pem',
        enable_control: true,
        enable_rpc: true,
        rpc_ntf: true,
        status_ntf: true,
      },
    });

    // Best-effort: cut the Shelly Cloud channel.
    if (onStage) onStage('config_cloud');
    try {
      await rpcCall(deviceId, 'Cloud.SetConfig', { config: { enable: false } });
    } catch (e) { warnings.push('כיבוי ענן Shelly נכשל: ' + e.message); }

    // Critical: join the site WiFi. We do NOT set ap:{enable:false}
    // here anymore — Shelly devices auto-disable the AP once the
    // station joins, and on Gen4 (Shelly 1 Gen4 specifically) sending
    // the combined sta+ap config caused the STA write to occasionally
    // not persist before the immediate reboot. Sending JUST the STA
    // block is safer; the AP turns itself off naturally a few seconds
    // later when sta_ip is allocated.
    if (onStage) onStage('config_wifi');
    await rpcCall(deviceId, 'Wifi.SetConfig', {
      config: { sta: { ssid, pass: password, enable: true } },
    });

    // POLL Wifi.GetStatus with the strict check (status=="got ip" + real
    // IP, not the truthy-string "0.0.0.0" that bit us before). Capture
    // the IP so we can use it as a second reboot path below.
    if (onStage) onStage('wifi_joining');
    const wifiDeadline = Date.now() + 30000;
    let joinedIp = '';
    while (Date.now() < wifiDeadline) {
      await sleep(2000);
      try {
        const st = await rpcCall(
          deviceId, 'Wifi.GetStatus', {}, { responseTimeoutMs: 4000 },
        );
        const ip = ((st && st.sta_ip) || '').trim();
        const status = ((st && st.status) || '').toLowerCase();
        const ipOk = ip && ip !== '0.0.0.0' && !ip.startsWith('169.254.');
        const statusOk = status === 'got ip' || status === 'got_ip';
        if (ipOk && statusOk) { joinedIp = ip; break; }
      } catch { /* transient — keep polling */ }
    }
    if (!joinedIp) {
      throw new Error(
        'הרכיב לא הצליח להתחבר ל-WiFi של האתר. בדוק שם רשת + סיסמה, ' +
        'או נסה את המסלול הידני.',
      );
    }

    // CRITICAL: on Shelly Gen4 fw 1.5.99 every SetConfig above (MQTT,
    // Cloud, Wifi) is held in RAM as "pending" until a reboot writes
    // them to flash. Without this reboot, the device's UI shows empty
    // MQTT settings, the broker never sees the client, and the wizard
    // hangs on "waiting for Shelly to connect to server" forever
    // (verified on hardware 2026-06-03). We fire the reboot via BLE
    // AND via HTTP-to-LAN-IP for redundancy — see rebootDevice docs.
    if (onStage) onStage('rebooting');
    await rebootDevice(deviceId, { ipAddr: joinedIp });
  } finally {
    try { await BleClient.disconnect(deviceId); } catch { /* link dropped by reboot */ }
  }

  // NOTE: the device UI password is deliberately NOT set here. It's
  // applied only after the open/close/lock tests pass (see
  // ``secureDevice``), so the Shelly stays freely accessible for
  // troubleshooting throughout provisioning + testing. We return the
  // realm (device id) so the caller can set auth later without another
  // GetDeviceInfo round-trip.
  return { warnings, realm };
}

// Final step, run only AFTER the install tests pass: lock the device's
// web/RPC UI with the per-device password (user "admin"). Kept separate
// from provisioning so the device stays open for troubleshooting until
// the gate is confirmed working. Best-effort — the caller treats a
// failure as a warning, not a blocked install.
export async function secureDevice(deviceId, { realm, password, onStage } = {}) {
  if (!realm || !password) throw new Error('חסרים פרטי אבטחה');
  await ensureBleReady();
  if (onStage) onStage('connecting');
  await connectWithRetry(deviceId, { tries: 8, delayMs: 3000 });
  try {
    if (onStage) onStage('config_auth');
    const ha1 = await sha256Hex(`admin:${realm}:${password}`);
    await rpcCall(deviceId, 'Shelly.SetAuth', { user: 'admin', realm, ha1 });
  } finally {
    try { await BleClient.disconnect(deviceId); } catch { /* ignore */ }
  }
}



